import { useState } from "react";
import { arvoresProximas } from "@/mocks/coletor";

interface FieldMapProps {
  onClose: () => void;
}

export default function FieldMap({ onClose }: FieldMapProps) {
  const [filtro, setFiltro] = useState("todas");
  const [selecionada, setSelecionada] = useState<typeof arvoresProximas[0] | null>(null);

  const filtros = [
    { key: "todas", label: "Todas" },
    { key: "pendentes", label: "Pendentes" },
    { key: "criticas", label: "Críticas" },
  ];

  const itensVisiveis = filtro === "criticas"
    ? arvoresProximas.filter((a) => a.risco === "Crítico" || a.risco === "Alto")
    : filtro === "pendentes"
    ? arvoresProximas.filter((a) => a.fitossanitario === "Ruim" || a.fitossanitario === "Crítico")
    : arvoresProximas;

  return (
    <div className="px-4 pt-4 pb-20 max-w-lg mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center">
          <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
        </button>
        <h2 className="text-lg font-heading font-bold text-[#3F4A52]">Mapa de Campo</h2>
      </div>

      {/* Mini mapa mockado */}
      <div className="rounded-xl border border-[#E8EAED] overflow-hidden bg-[#E8EAED]/30 h-56 mb-4 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-map-pin-line text-4xl text-[#1B6E4F]" />
            <p className="text-xs text-[#3F4A52]/50 mt-1">Sua localização</p>
            <p className="text-[10px] text-[#3F4A52]/30 mt-0.5 font-mono">-16.6802, -49.2563</p>
          </div>
        </div>
        {/* Pontos mockados na borda do mapa */}
        {[{ top: "20%", left: "15%", color: "bg-[#1B6E4F]" }, { top: "35%", left: "60%", color: "bg-[#F57C00]" }, { top: "55%", left: "30%", color: "bg-[#D32F2F]" }, { top: "70%", left: "70%", color: "bg-[#1B6E4F]" }, { top: "15%", left: "80%", color: "bg-[#FBC02D]" }].map((p, i) => (
          <div key={i} className={`absolute w-2.5 h-2.5 ${p.color} rounded-full border-2 border-white shadow-sm`} style={{ top: p.top, left: p.left }} />
        ))}
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pb-1">
        {filtros.map((f) => (
          <button
            key={f.key}
            onClick={() => setFiltro(f.key)}
            className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              filtro === f.key ? "bg-[#0052CC] text-white" : "bg-white border border-[#E8EAED] text-[#3F4A52]"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button className="px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-[#1B6E4F] text-white">
          <i className="ri-add-line mr-0.5" /> Cadastrar aqui
        </button>
      </div>

      {/* Lista de árvores */}
      <div>
        <p className="text-xs text-[#3F4A52]/60 mb-2">{itensVisiveis.length} árvores</p>
        <div className="space-y-2 max-h-[calc(100vh-450px)] overflow-y-auto">
          {itensVisiveis.map((a) => {
            const riscoColor = a.risco === "Crítico" ? "bg-[#D32F2F]" : a.risco === "Alto" ? "bg-[#F57C00]" : a.risco === "Médio" ? "bg-[#FBC02D]" : "bg-[#1B6E4F]";
            return (
              <button
                key={a.id}
                onClick={() => setSelecionada(a)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                  selecionada?.id === a.id ? "border-[#0052CC] bg-[#64B5F6]/10" : "border-[#E8EAED] bg-white"
                }`}
              >
                <div className="relative w-10 h-10 rounded-lg bg-[#E8EAED] flex items-center justify-center flex-shrink-0">
                  <i className="ri-leaf-line text-[#1B6E4F] text-lg" />
                  <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${riscoColor} border border-white`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#3F4A52] truncate">{a.especie}</p>
                  <p className="text-xs text-[#3F4A52]/50 font-mono">{a.id}</p>
                  <p className="text-xs text-[#3F4A52]/50">{a.bairro} · {a.risco}</p>
                </div>
                <i className="ri-arrow-right-s-line text-[#3F4A52]/30" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Detalhe */}
      {selecionada && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-2xl p-5 max-w-lg mx-auto space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-heading font-bold text-[#3F4A52]">{selecionada.especie}</h3>
              <button onClick={() => setSelecionada(null)} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center">
                <i className="ri-close-line text-[#3F4A52]" />
              </button>
            </div>
            <p className="text-xs font-mono text-[#0052CC]">{selecionada.id}</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-[#3F4A52]/60">Bairro:</span> <span className="font-medium text-[#3F4A52]">{selecionada.bairro}</span></div>
              <div><span className="text-[#3F4A52]/60">Risco:</span> <span className="font-medium text-[#3F4A52]">{selecionada.risco}</span></div>
              <div><span className="text-[#3F4A52]/60">Fitossanidade:</span> <span className="font-medium text-[#3F4A52]">{selecionada.fitossanitario}</span></div>
              <div><span className="text-[#3F4A52]/60">Coordenadas:</span> <span className="font-mono text-xs">{selecionada.latitude}, {selecionada.longitude}</span></div>
            </div>
            <div className="flex gap-2 pt-2">
              <button className="flex-1 py-3 rounded-xl bg-[#0052CC] text-white font-heading font-bold text-sm">Nova vistoria</button>
              <button className="flex-1 py-3 rounded-xl border border-[#E8EAED] text-sm font-medium text-[#3F4A52]">Adicionar foto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}